import { BehaviorSubject } from "rxjs";
import Router from "next/router";
import { fetchWrapper } from "src/helpers";
import { alertService } from "src/services";
import { User } from "firebase/auth";

const baseUrl = "/api/users";

const userSubject = new BehaviorSubject(
  typeof window !== "undefined" &&
    localStorage.getItem("BetaUser") !== null &&
    JSON.parse(localStorage.getItem("BetaUser") || ""),
);

const userLogin = async (params: User) => {
  const deviceId = getDeviceId();
  const user = await fetchWrapper.post(`${baseUrl}/register`, {
    uid: params.uid,
    email: params.email,
    displayName: params.displayName,
    phoneNumber: params.phoneNumber,
    providerData: params.providerData,
    device_id: deviceId,
    platform: 3,
  });
  const userValue = {
    uid: user.uid,
    firstName: params.displayName || "ABillion User",
    email: user.email || params.email,
    name: user.name,
    customer_email: user.customer_email,
    phone_number: user.phone_number,
    token: user.token,
    session_id: user.sessionId,
    is_subscribed: user.is_subscribed,
    is_free: user.is_free,
    planExpiry: user.planExpiry,
    plan: user.plan,
    latestSymbol: user.latestSymbol,
    is_trial: user.is_trial,
    next_working_day: user.next_working_day,
    next_market_day: user.next_market_day,
    last_source_date: user.last_source_date,
    free_user_source_date: user.free_user_source_date,
    risk_profile: user.risk_profile,
  };
  userSubject.next(userValue);
  localStorage.setItem("BetaUser", JSON.stringify(userValue));
  return userValue;
};

const getDeviceId = () => {
  if (typeof window === "undefined") return undefined;
  let deviceId = localStorage.getItem("abillion_device_id");
  if (!deviceId) {
    deviceId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    localStorage.setItem("abillion_device_id", deviceId);
  }
  return deviceId;
};

const syncFcmToken = async (
  fcmToken?: string,
  authToken?: string,
  force = false,
) => {
  const user = userSubject.value;
  const currentToken = authToken || user?.token;
  if (!currentToken) return;

  const deviceId = getDeviceId();
  const userId = user?.uid || "unknown";

  const currentState = `${userId}:${deviceId}:${fcmToken || "none"}`;
  const lastSyncedState = localStorage.getItem("last_fcm_sync_state");

  if (!force && lastSyncedState === currentState) {
    return;
  }

  try {
    await fetchWrapper.post(`${baseUrl}/fcmToken`, {
      device_token: fcmToken,
      device_id: deviceId,
      platform: 3, // Web
    });

    localStorage.setItem("last_fcm_sync_state", currentState);

    if (fcmToken) {
      localStorage.setItem("last_synced_fcm_token", fcmToken);
      localStorage.setItem("fcm_token_synced", "true");
    } else {
      localStorage.removeItem("last_synced_fcm_token");
      localStorage.removeItem("fcm_token_synced");
    }
  } catch (error) {
    console.error("FCM token sync failed:", error);
  }
};

const removeFcmToken = async () => {
  const fcmToken = localStorage.getItem("fcm_token");
  const userStr = localStorage.getItem("BetaUser");
  if (!fcmToken || !userStr) return;

  try {
    const user = JSON.parse(userStr);
    if (!user?.token) return;
    await fetchWrapper.delete(
      `${baseUrl}/fcmToken`,
      {
        device_token: fcmToken,
      },
      {},
      undefined,
      { skipAuthLogout: true },
    );
    localStorage.removeItem("fcm_token_synced");
    localStorage.removeItem("last_synced_fcm_token");
  } catch (error) {
    console.error("FCM token removal failed:", error);
  }
};

let isLoggingOut = false;

const logout = async () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  alertService.clear();

  const currentUser = userSubject.value;

  try {
    await removeFcmToken();
  } catch (error) {
    console.error("FCM token removal failed:", error);
  }

  try {
    await fetchWrapper.delete(
      `${baseUrl}/session`,
      {
        session_id: currentUser?.session_id,
        device_id: getDeviceId(),
      },
      {},
      undefined,
      {
        skipAuthLogout: true,
      },
    );
  } catch (error) {
    console.error("Session revoke failed:", error);
  }
  localStorage.removeItem("BetaUser");
  localStorage.removeItem("dark");
  sessionStorage.removeItem("lastSearchCompanyDataDriven");
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith("lastSearchCompanyDataDriven_")) {
      sessionStorage.removeItem(key);
    }
  }
  sessionStorage.removeItem("selectedDate");
  sessionStorage.removeItem("lastSearchCompany");
  sessionStorage.removeItem("investmentCapital");
  localStorage.removeItem("portfolioColumnVisibility");
  localStorage.removeItem("stock_applied_filters");
  localStorage.removeItem("stock_active_tab");
  localStorage.removeItem("sector_panel_filters");
  localStorage.removeItem("selected_month");
  localStorage.removeItem("selectedDate");
  localStorage.removeItem("isLoggedInWith");
  localStorage.removeItem("last_fcm_sync_state");
  localStorage.removeItem("abillion_device_id");
  localStorage.removeItem("news_searchQuery");
  localStorage.removeItem("news_selectedStocks");
  localStorage.removeItem("news_impactLevel");
  localStorage.removeItem("news_sentiment");
  localStorage.removeItem("news_timePeriod");
  localStorage.removeItem("news_preference");
  localStorage.removeItem("news_activeTab");
  localStorage.removeItem("portfolio-timeline-end-date");
  localStorage.removeItem("portfolio-timeline-start-date");
  localStorage.removeItem("portfolio-timeline-filter");
  localStorage.removeItem("portfolio-timeline-search");
  localStorage.removeItem("portfolio-timeline-range-preset");
  localStorage.removeItem("queryParamPlanId");
  localStorage.removeItem("queryParamCouponCode");
  localStorage.removeItem("cmsCheckoutToken");
  localStorage.removeItem("cmsCheckoutIntent");
  localStorage.removeItem("last_selected_portfolio_folders");
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("candle_legend_selected_")) {
      localStorage.removeItem(key);
    }
  });
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("rsi_legend_selected_")) {
      localStorage.removeItem(key);
    }
  });
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("area_legend_selected_")) {
      localStorage.removeItem(key);
    }
  });

  userSubject.next(null);
  isLoggingOut = false;
  console.log("[multi-device] client_logout_done");
  Router.push("/");
};

export const userService = {
  get userValue() {
    return userSubject.value;
  },
  get loggingOut() {
    return isLoggingOut;
  },
  userLogin,
  logout,
  syncFcmToken,
};
